import { createBrowserRouter, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { MainLayout } from "@/layouts/main-layout";
import { ChatLayout } from "@/layouts/chat-layout";
import { AuthLayout } from "@/layouts/auth-layout";
import { AuthProvider } from "@/contexts/auth-context";
import { ScrollToTop } from "@/components/common";
import { Toaster } from "@/components/ui/toaster";
import { LandingPage } from "@/pages/landing/landing-page";
import { LoginPage } from "@/pages/login/login-page";
import { SignupPage } from "@/pages/signup/signup-page";
import { EmailVerificationPage } from "@/pages/email-verification/email-verification-page";
import { TermsPage } from "@/pages/terms/terms-page";
import { PrivacyPage } from "@/pages/privacy/privacy-page";
import HomePage from "@/pages/user/home-page";
import ProjectListPage from "@/pages/projects/project-list-page";
import MyPage from "@/pages/my-page/my-page";
import AgentListPage from "@/pages/my-agent/my-agent-list";
import NewAgentPage from "@/pages/my-agent/new-agent";
import PublicAgentListPage from "@/pages/agent-list/agent-list-page";
import AgentDetailPage from "@/pages/agent-list/agent-detail-page";
import { ChatPage } from "@/pages/chat/chat-page";
import DateTimePickerTestPage from "@/pages/test/date-time-picker-test-page";
import ProjectReportPage from "@/pages/project-report/project-report-page";
import ShortlistPage from "@/pages/shortlist/shortlist-page";
import AgentComparePage from "@/pages/compare-agent/agent-compare-page";
import MyAgentDetailPage from "@/pages/my-agent/my-agent-detail-page";
import { EditAgentPage } from "./pages/my-agent/edit-agent";
import { useAuth } from "@/contexts/auth-context";
import ContactPage from "./pages/contact/contact-page";
import InquiryListPage from "./pages/inquiries/inquiry-list-page";
import InquiryDetailPage from "./pages/inquiries/inquiry-detail-page";
import AXNews from "./pages/ax-news/Ax-news";
import AXListPage from "./pages/ax-news/ax-list-page";
import AxNewsDetail from "./pages/ax-news/ax-news-detail";
import { NotFoundPage } from "./pages/error/not-found-page";
import { RouterErrorElement } from "./pages/error/router-error-element";
import { ServerErrorRoute } from "./pages/error/server-error-route";
import ProjectReportChecklist from "./pages/project-report/project-report-checklist";

function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = window.location;

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <Outlet />;
}

function GlobalNavigationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleServerError = (event: any) => {
      const status = event.detail?.status || 500;
      navigate(`/server-error?status=${status}`, { replace: true });
    };

    const handleNotFound = () => {
      navigate("/404", { replace: true });
    };

    window.addEventListener("server:error", handleServerError);
    window.addEventListener("server:not-found", handleNotFound);

    return () => {
      window.removeEventListener("server:error", handleServerError);
      window.removeEventListener("server:not-found", handleNotFound);
    };
  }, [navigate]);

  return null;
}

export const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollToTop />
        <AuthProvider>
          <GlobalNavigationListener />
          <Outlet />
        </AuthProvider>
        <Toaster />
      </>
    ),
    errorElement: <RouterErrorElement />,
    children: [
      {
        element: <MainLayout />,
        children: [
          { path: "/", element: <LandingPage /> },
          { path: "/terms", element: <TermsPage /> },
          { path: "/privacy", element: <PrivacyPage /> },
          { path: "/ax-news", element: <AXNews /> },
          { path: "/ax-news/list", element: <AXListPage /> },
          { path: "/ax-news/list/:id", element: <AxNewsDetail /> },
          // { path: "/login", element: <LoginPage /> },
          // { path: "/signup", element: <SignupPage /> },
          // { path: "/email-verification", element: <EmailVerificationPage /> },
          {
            element: <ProtectedRoute />,
            children: [
              { path: "/contact", element: <ContactPage /> },
              { path: "/projects", element: <ProjectListPage /> },
              { path: "/my-page", element: <MyPage /> },
              { path: "/my-agents", element: <AgentListPage /> },
              { path: "/my-agents/new", element: <NewAgentPage /> },
              { path: "/my-agents/:id/detail", element: <MyAgentDetailPage /> },
              { path: "/my-agents/:id/edit", element: <EditAgentPage /> },
              { path: "/inquiries", element: <InquiryListPage /> },
              { path: "/inquiries/:id", element: <InquiryDetailPage /> },
              { path: "/agents", element: <PublicAgentListPage /> },
              { path: "/agents/:id", element: <AgentDetailPage /> },
              { path: "/projects/checklist", element: <ProjectReportChecklist /> },
              { path: "/projects/:id/report", element: <ProjectReportPage /> },
              { path: "/projects/:id/shortlist", element: <ShortlistPage /> },
              { path: "/compare-agent", element: <AgentComparePage /> },
              {
                path: "/test-datetime-picker",
                element: <DateTimePickerTestPage />,
              },
            ],
          },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/signup", element: <SignupPage /> },
          { path: "/email-verification", element: <EmailVerificationPage /> },
        ],
      },
      {
        element: <ChatLayout />,
        children: [
          {
            element: <ProtectedRoute />,
            children: [{ path: "/chat", element: <ChatPage /> }],
          },
        ],
      },

      {
        element: <ProtectedRoute />,
        children: [{ path: "/home", element: <HomePage /> }],
      },
      { path: "/404", element: <NotFoundPage /> },
      { path: "/server-error", element: <ServerErrorRoute /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
